import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ArticlesGrid from '../components/ArticlesGrid';
import Pagination from '../components/Pagination';
import { useSiteData } from '../data/SiteDataContext';
import { normalizeTagValue } from '../../utils/tagUtils';
import { classifyGroups, collectGroupTags } from '../lib/tagIndex';
import { POSTS_PER_PAGE } from './HomePage';

// Single-tag archive, laid out exactly like CategoryPage: eyebrow, title,
// article grid, pagination. Sidebar geography/persons rows and article
// "filed under" chips land here.
function TagArchivePage() {
    const { tag, pageNum } = useParams();
    const { status, posts, groups } = useSiteData();

    const normalizedTag = normalizeTagValue(tag || '');

    const { geography, persons } = useMemo(() => classifyGroups(groups), [groups]);

    // The eyebrow names the index bucket the tag belongs to, falling back to
    // the generic "Tag" for subject-level tags.
    const eyebrow = useMemo(() => {
        const inBucket = (bucket) => (
            collectGroupTags(bucket).some((entry) => entry.value === normalizedTag)
        );
        if (inBucket(geography)) return 'Geography';
        if (inBucket(persons)) return 'Persons';
        return 'Tag';
    }, [geography, persons, normalizedTag]);

    const tagPosts = useMemo(() => posts.filter((post) => (
        Array.isArray(post.tags)
        && post.tags.some((entry) => normalizeTagValue(entry) === normalizedTag)
    )), [posts, normalizedTag]);

    if (!normalizedTag) {
        return <Navigate to="/tags" replace />;
    }

    if (status === 'loading') {
        return <p className="small-meta site-loading">Loading …</p>;
    }

    if (status === 'error') {
        return <p className="small-meta site-loading">Failed to load documents. Refresh to retry.</p>;
    }

    const base = `/tag/${encodeURIComponent(normalizedTag)}`;
    const totalPages = Math.max(1, Math.ceil(tagPosts.length / POSTS_PER_PAGE));
    const requestedPage = pageNum ? parseInt(pageNum, 10) : 1;
    const currentPage = Number.isFinite(requestedPage) ? requestedPage : 1;

    if (pageNum && (!Number.isFinite(requestedPage) || requestedPage < 2)) {
        return <Navigate to={base} replace />;
    }

    if (currentPage > totalPages) {
        return <Navigate to={base} replace />;
    }

    const start = (currentPage - 1) * POSTS_PER_PAGE;
    const pagePosts = tagPosts.slice(start, start + POSTS_PER_PAGE);

    return (
        <>
            <header className="archive-header">
                <div className="eyebrow" style={{ marginBottom: '.4rem' }}>{eyebrow}</div>
                <h1>{normalizedTag}</h1>
            </header>
            {tagPosts.length === 0 ? (
                <p className="small-meta site-loading">
                    No documents carry this tag.{' '}
                    <Link to="/tags" style={{ textDecoration: 'underline' }}>Browse all tags →</Link>
                </p>
            ) : (
                <>
                    <ArticlesGrid posts={pagePosts} />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        getPageLink={(page) => (page <= 1 ? base : `${base}/page/${page}`)}
                        ariaLabel="Tag archive pagination"
                    />
                </>
            )}
        </>
    );
}

export default TagArchivePage;
